import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getSessions,
  getSessionTrace,
  setSessionState,
  SESSION_STATES,
  type SessionListItem,
  type SessionTrace,
  type SessionEventRow,
  type SessionStateName,
  type UserRole,
} from '../api/client';

// ── lifecycle stage → colour (the "stage" badge) ───────────────────────────
const STAGE_COLOR: Record<string, string> = {
  created: '#6b7280',
  uploading: '#6b7280',
  selecting: '#6b7280',
  awaiting_payment: '#d97706',
  code_issued: '#d97706',
  paid: '#2563eb',
  printing: '#7c3aed',
  completed: '#16a34a',
  cancelled: '#9ca3af',
  expired: '#9ca3af',
  failed: '#dc2626',
  refunded: '#dc2626',
};
const stageColor = (s: string) => STAGE_COLOR[s] ?? '#6b7280';

// terminal → colour (web vs kiosk vs system) for the timeline
const termColor = (t: string) =>
  t === 'web' ? '#2563eb' : t.startsWith('kiosk') ? '#ea580c' : '#6b7280';

// event_type → friendly label + colour (the timeline dot/label). Covers the
// full OPS-16 trail vocabulary: web + kiosk lifecycle, payments, print, cash,
// change, and terminal states. Unknown types fall back to the raw string / grey.
const EVENT_META: Record<string, { label: string; color: string }> = {
  session_created: { label: 'Session created', color: '#6b7280' },
  file_uploaded: { label: 'File uploaded', color: '#6b7280' },
  document_processed: { label: 'Document processed', color: '#6b7280' },
  document_rejected: { label: 'Document rejected', color: '#dc2626' },
  selection_locked: { label: 'Selection locked', color: '#0ea5e9' },
  payment_method_selected: { label: 'Payment method selected', color: '#d97706' },
  payment_intent_created: { label: 'Payment intent created', color: '#d97706' },
  payment_succeeded: { label: 'Payment succeeded', color: '#16a34a' },
  card_paid: { label: 'Card paid', color: '#16a34a' },
  card_session_created: { label: 'Card session created', color: '#d97706' },
  cash_collected: { label: 'Cash collected', color: '#16a34a' },
  cash_inserted: { label: 'Cash inserted', color: '#0891b2' },
  change_dispensed: { label: 'Change dispensed', color: '#0891b2' },
  change_unavailable: { label: 'Change unavailable', color: '#dc2626' },
  code_issued: { label: 'Code issued', color: '#7c3aed' },
  redeemed_at_kiosk: { label: 'Redeemed at kiosk', color: '#7c3aed' },
  print_started: { label: 'Print started', color: '#7c3aed' },
  print_completed: { label: 'Print completed', color: '#16a34a' },
  print_failed: { label: 'Print failed', color: '#dc2626' },
  session_cancelled: { label: 'Session cancelled', color: '#9ca3af' },
  session_expired: { label: 'Session expired', color: '#9ca3af' },
  refunded: { label: 'Refunded', color: '#dc2626' },
  email_sent: { label: 'Email sent', color: '#6b7280' },
};
const eventMeta = (t: string) => EVENT_META[t] ?? { label: t, color: '#6b7280' };

function sessionType(s: SessionListItem): { label: string; color: string } {
  if (s.createdTerminal === 'web') return { label: 'Online', color: '#2563eb' };
  if (s.origin === 'local') return { label: 'Offline', color: '#ea580c' };
  if (s.createdTerminal.startsWith('kiosk')) return { label: 'Kiosk', color: '#ea580c' };
  return { label: s.createdTerminal || '—', color: '#6b7280' };
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}
function fmtAgo(iso: string | null): string {
  if (!iso) return '';
  // Postgres timestamptz ("… 09:39:42+00") needs the space→T swap AND the bare
  // "+00" offset padded to "+00:00", or Date() returns Invalid.
  const d = new Date(iso.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00')).getTime();
  if (Number.isNaN(d)) return '';
  const s = Math.max(0, Math.round((Date.now() - d) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

const Badge = ({ text, color }: { text: string; color: string }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: 12,
      fontWeight: 600,
      color: '#fff',
      background: color,
      whiteSpace: 'nowrap',
    }}
  >
    {text}
  </span>
);

const STAGES = [
  'created', 'uploading', 'selecting', 'awaiting_payment', 'paid',
  'code_issued', 'printing', 'completed', 'cancelled', 'expired', 'failed', 'refunded',
];

export function SessionsPage({ role }: { role?: UserRole } = {}) {
  const [rows, setRows] = useState<SessionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [auto, setAuto] = useState(true);
  const [machine, setMachine] = useState('');
  const [state, setState] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [trace, setTrace] = useState<SessionTrace | null>(null);
  const [traceErr, setTraceErr] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await getSessions({
        limit: 100,
        machineId: machine ? Number(machine) : undefined,
        state: state || undefined,
      });
      setRows(data);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [machine, state]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!auto) { if (timer.current) window.clearInterval(timer.current); return; }
    timer.current = window.setInterval(() => void load(), 10000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [auto, load]);

  const openTrace = useCallback(async (code: string) => {
    setSelected(code);
    setTrace(null);
    setTraceErr(null);
    try {
      setTrace(await getSessionTrace(code));
    } catch (e) {
      setTraceErr(e instanceof Error ? e.message : 'Not found');
    }
  }, []);

  // Ссылка вида /sessions?code=12345678 (из отзыва, из алерта) сразу открывает таймлайн.
  const [params] = useSearchParams();
  const codeParam = params.get('code');
  useEffect(() => { if (codeParam) void openTrace(codeParam); }, [codeParam, openTrace]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)', gap: 16 }}>
      {/* ── LEFT: live list ─────────────────────────────────────────── */}
      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Sessions (live)</h2>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> auto 10s
          </label>
          <button onClick={() => void load()} style={{ fontSize: 13 }}>↻ Refresh</button>
          <span style={{ flex: 1 }} />
          <input
            placeholder="machine id"
            value={machine}
            onChange={(e) => setMachine(e.target.value.replace(/\D/g, ''))}
            style={{ width: 90, fontSize: 13, padding: '4px 6px' }}
          />
          <select value={state} onChange={(e) => setState(e.target.value)} style={{ fontSize: 13, padding: '4px 6px' }}>
            <option value="">all stages</option>
            {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); if (search.trim()) void openTrace(search.trim().toUpperCase()); }}
          style={{ display: 'flex', gap: 8, marginBottom: 12 }}
        >
          <input
            placeholder="open session code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, fontSize: 13, padding: '6px 8px' }}
          />
          <button type="submit" style={{ fontSize: 13 }}>Open</button>
        </form>

        {err && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>⚠ {err}</div>}
        {loading ? (
          <div style={{ color: '#6b7280' }}>Loading…</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '6px 8px' }}>Code</th>
                  <th style={{ padding: '6px 8px' }}>Type</th>
                  <th style={{ padding: '6px 8px' }}>Machine</th>
                  <th style={{ padding: '6px 8px' }}>Stage</th>
                  <th style={{ padding: '6px 8px' }}>Last event</th>
                  <th style={{ padding: '6px 8px' }}>When</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const ty = sessionType(r);
                  const isSel = selected === r.code;
                  return (
                    <tr
                      key={r.id}
                      onClick={() => r.code && void openTrace(r.code)}
                      style={{
                        cursor: r.code ? 'pointer' : 'default',
                        background: isSel ? '#eff6ff' : undefined,
                        borderBottom: '1px solid #f3f4f6',
                      }}
                    >
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontWeight: 600 }}>{r.code ?? '—'}</td>
                      <td style={{ padding: '6px 8px' }}><Badge text={ty.label} color={ty.color} /></td>
                      <td style={{ padding: '6px 8px' }}>{r.machineId ?? '—'}</td>
                      <td style={{ padding: '6px 8px' }}><Badge text={r.state} color={stageColor(r.state)} /></td>
                      <td style={{ padding: '6px 8px', color: '#374151' }}>{r.lastEventType ?? '—'}</td>
                      <td style={{ padding: '6px 8px', color: '#6b7280' }}>{fmtAgo(r.lastEventAt ?? r.createdAt)}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 16, color: '#9ca3af' }}>No sessions.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── RIGHT: per-session timeline ─────────────────────────────── */}
      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>
          Timeline {selected && <span style={{ fontFamily: 'monospace' }}>· {selected}</span>}
        </h2>
        {!selected && <div style={{ color: '#9ca3af', fontSize: 13 }}>Select a session on the left, or enter a code.</div>}
        {selected && traceErr && <div style={{ color: '#dc2626', fontSize: 13 }}>⚠ {traceErr}</div>}
        {selected && !trace && !traceErr && <div style={{ color: '#6b7280' }}>Loading…</div>}
        {trace && (
          <Timeline
            trace={trace}
            canEdit={role === 'superadmin'}
            onChanged={() => { if (selected) void openTrace(selected); void load(); }}
          />
        )}
      </div>
    </div>
  );
}

function Timeline({
  trace,
  canEdit,
  onChanged,
}: {
  trace: SessionTrace;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const s = trace.session;
  const ty = sessionType(s);
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <Badge text={ty.label} color={ty.color} />
        <Badge text={s.state} color={stageColor(s.state)} />
        {s.machineId != null && <Badge text={`machine ${s.machineId}`} color="#374151" />}
        {s.priceCents != null && <Badge text={`${Math.round(s.priceCents / 100)} Kč`} color="#374151" />}
        {s.paymentMethod && <Badge text={s.paymentMethod} color="#374151" />}
        {s.refunded && <Badge text="refunded" color="#dc2626" />}
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
        start: {fmtTime(s.createdAt)} · origin: {s.origin} · terminal: {s.createdTerminal}
        {s.email ? ` · ${s.email}` : ''}
      </div>

      {canEdit && s.code && (
        <StateOverride code={s.code} current={s.state} onChanged={onChanged} />
      )}

      <div style={{ borderLeft: '2px solid #e5e7eb', paddingLeft: 14, marginLeft: 4 }}>
        {trace.events.map((e) => <EventRow key={e.seq} e={e} />)}
        {trace.events.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>No events.</div>}
      </div>
    </div>
  );
}

/**
 * Set the state by hand (superadmin). A drifted state makes the kiosk refuse a
 * paid code — this puts the customer back on track without touching the DB.
 */
function StateOverride({
  code,
  current,
  onChanged,
}: {
  code: string;
  current: string;
  onChanged: () => void;
}) {
  const [next, setNext] = useState<SessionStateName>(current as SessionStateName);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => { setNext(current as SessionStateName); setOk(null); setErr(null); }, [current, code]);

  const apply = async () => {
    setBusy(true); setErr(null); setOk(null);
    try {
      const r = await setSessionState(code, next, reason.trim() || undefined);
      setOk(`${r.from} → ${r.to}`);
      setReason('');
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to change state');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 14,
      background: '#fafafa',
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
        Change state
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={next}
          onChange={(e) => setNext(e.target.value as SessionStateName)}
          disabled={busy}
          style={{ padding: '6px 8px', fontSize: 13 }}
        >
          {SESSION_STATES.map((st) => (
            <option key={st} value={st}>{st}</option>
          ))}
        </select>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="reason (optional)"
          disabled={busy}
          style={{ padding: '6px 8px', fontSize: 13, flex: '1 1 160px', minWidth: 0 }}
        />
        <button
          onClick={() => void apply()}
          disabled={busy || next === current}
          style={{ padding: '6px 12px', fontSize: 13 }}
        >
          {busy ? 'Applying…' : 'Apply'}
        </button>
      </div>
      {err && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>⚠ {err}</div>}
      {ok && <div style={{ color: '#16a34a', fontSize: 12, marginTop: 8 }}>✓ {ok}</div>}
      <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 8 }}>
        Recorded in the timeline as <code>state_overridden</code>.
      </div>
    </div>
  );
}

function EventRow({ e }: { e: SessionEventRow }) {
  const [open, setOpen] = useState(false);
  const hasPayload = e.payload != null && Object.keys(e.payload as object).length > 0;
  const meta = eventMeta(e.type);
  return (
    <div style={{ position: 'relative', marginBottom: 12 }}>
      <span
        style={{
          position: 'absolute', left: -21, top: 3, width: 10, height: 10,
          borderRadius: '50%', background: meta.color, border: '2px solid #fff',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: meta.color }} title={e.type}>{meta.label}</span>
        <Badge text={e.terminal} color={termColor(e.terminal)} />
        <span style={{ fontSize: 11, color: '#9ca3af' }}>{e.actor}</span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: '#9ca3af' }}>{fmtTime(e.occurredAt)}</span>
        {hasPayload && (
          <button onClick={() => setOpen((v) => !v)} style={{ fontSize: 11, padding: '0 6px' }}>
            {open ? '−' : '⋯'}
          </button>
        )}
      </div>
      {open && hasPayload && (
        <pre style={{
          margin: '4px 0 0', fontSize: 11, background: '#f9fafb', padding: 8,
          borderRadius: 6, overflowX: 'auto', color: '#374151',
        }}>
          {JSON.stringify(e.payload, null, 2)}
        </pre>
      )}
    </div>
  );
}
